/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  useCallback,
  useFocusOut,
  useRef,
  useState,
} from '@web-stories-wp/react';
import { __ } from '@web-stories-wp/i18n';
import {
  NumericInput,
  Icons,
  ToggleButton,
  BUTTON_SIZES,
  BUTTON_VARIANTS,
  PLACEMENT,
  useKeyDownEffect,
} from '@web-stories-wp/design-system';
import { hasGradient } from '@web-stories-wp/patterns';

/**
 * Internal dependencies
 */
import { useFont } from '../../../../app/font';
import Tooltip from '../../../tooltip';
import stripHTML from '../../../../utils/stripHTML';
import clamp from '../../../../utils/clamp';
import { Row, usePresubmitHandler } from '../../../form';
import { MULTIPLE_VALUE, MULTIPLE_DISPLAY_VALUE } from '../../../../constants';
import {
  focusStyle,
  getCommonValue,
  inputContainerStyleOverride,
} from '../../shared';
import useRichTextFormatting from './useRichTextFormatting';
import elementIs from '../../../../elements/utils/elementIs';
import { getTextDepth } from '../../../../elements/text/utils/getTextDepth';
import { getTextInnerGlow } from '../../../../elements/text/utils/getTextInnerGlow';
import {
  hasMixedTypographyMarkers,
  hasTextBorder,
} from '../../../../elements/text/utils';
import Popup from '../../../popup';
import useInspector from '../../../inspector/useInspector';
import useFocusTrapping from '../../../../utils/useFocusTrapping';
import ListControls, { LIST_SETTINGS_NESTED_POPUP_ID } from './list';

const MIN_MAX = {
  LINE_HEIGHT: {
    MIN: 0,
    MAX: 10,
  },
  LETTER_SPACING: {
    MIN: -300,
    MAX: 300,
  },
};

const StyledNumericInput = styled(NumericInput)`
  flex-grow: 1;
`;

const Space = styled.div`
  flex: 0 0 10px;
`;

const StyledToggle = styled(ToggleButton)`
  ${focusStyle};
`;

const InlineRow = styled(Row)`
  margin-bottom: 0;
`;

const InlineFormattingRow = styled(Row)`
  margin-bottom: 4px;
`;

const ListButtonAnchor = styled.span`
  display: flex;
`;

const ListSettingsDialog = styled.div`
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bg.secondary};
  width: 330px;
  user-select: none;
  padding: 16px;
`;

const LIST_SETTINGS_DIALOG_ID = 'text-list-settings-dialog';
const LIST_SETTINGS_SPACING = { x: 20 };

function ListSettingsPopup({ anchor, children, onClose }) {
  const containerRef = useRef();
  const previousFocus = useRef(document.activeElement);

  const handleCloseAndRefocus = useCallback(() => {
    previousFocus.current?.focus();
    onClose();
  }, [onClose]);

  useFocusOut(
    containerRef,
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        onClose();
        return;
      }

      const targetIsInsidePopup = containerRef.current?.contains(target);
      const targetIsTrigger = anchor.current?.contains(target);
      const targetIsNestedDropdown = target.closest(
        `#${LIST_SETTINGS_NESTED_POPUP_ID}`
      );
      const targetIsNestedDialog = target.closest('[role="dialog"]');

      if (
        !targetIsInsidePopup &&
        !targetIsTrigger &&
        !targetIsNestedDropdown &&
        !targetIsNestedDialog
      ) {
        onClose();
      }
    },
    [anchor, onClose]
  );
  useKeyDownEffect(containerRef, 'esc', handleCloseAndRefocus);
  useFocusTrapping({ ref: containerRef });

  return (
    <ListSettingsDialog
      id={LIST_SETTINGS_DIALOG_ID}
      role="dialog"
      aria-label={__('Bulleted list settings', 'web-stories')}
      ref={containerRef}
    >
      {children}
    </ListSettingsDialog>
  );
}

ListSettingsPopup.propTypes = {
  anchor: PropTypes.shape({ current: PropTypes.instanceOf(Element) })
    .isRequired,
  children: PropTypes.node.isRequired,
  onClose: PropTypes.func.isRequired,
};

function Toggle(props) {
  return (
    <StyledToggle
      size={BUTTON_SIZES.SMALL}
      variant={BUTTON_VARIANTS.SQUARE}
      {...props}
    />
  );
}

function StylePanel({ selectedElements, pushUpdate }) {
  const [listSettingsOpen, setListSettingsOpen] = useState(false);
  const listButtonRef = useRef();
  const {
    actions: { maybeEnqueueFontStyle },
  } = useFont();
  const {
    refs: { inspector },
  } = useInspector();
  const verticalTrim = getCommonValue(selectedElements, 'verticalTrim');
  const textAlign = getCommonValue(selectedElements, 'textAlign');
  const globalLineHeight = getCommonValue(selectedElements, 'lineHeight');

  const {
    textInfo: {
      isBold,
      isItalic,
      isUnderline,
      isUppercase,
      hasList,
      isUnorderedList,
      isListMixed,
      letterSpacing,
      fontWeight,
      lineHeight: inlineLineHeight,
    },
    handlers: {
      handleClickBold,
      handleClickItalic,
      handleClickUnderline,
      handleClickUppercase,
      handleRemoveList: handleRemoveListFormatting,
      handleApplyUnorderedList,
      handleSetLetterSpacing,
      handleSetLineHeight,
    },
    isEditingMixedTypography,
  } = useRichTextFormatting(selectedElements, pushUpdate);
  const lineHeight = isEditingMixedTypography
    ? inlineLineHeight
    : globalLineHeight;
  const isInlineLineHeightDisabled =
    isEditingMixedTypography && hasList === true;
  const lineHeightEditInteractionRef = useRef(false);

  const setLetterSpacingMinMax = useCallback(
    (evt, value) =>
      handleSetLetterSpacing(clamp(value, MIN_MAX.LETTER_SPACING)),
    [handleSetLetterSpacing]
  );

  const handleLineHeightFocus = useCallback(() => {
    lineHeightEditInteractionRef.current = isEditingMixedTypography;
  }, [isEditingMixedTypography]);

  const handleLineHeightChange = useCallback(
    (evt, value) => {
      if (isEditingMixedTypography) {
        lineHeightEditInteractionRef.current = true;
      }
      handleSetLineHeight(value, false);
    },
    [handleSetLineHeight, isEditingMixedTypography]
  );

  const handleLineHeightBlur = useCallback(
    (evt, value) => {
      const beganWhileEditing = lineHeightEditInteractionRef.current;
      // NumericInput also invokes onBlur for Enter without actually removing
      // focus. Keep the interaction scoped to inline editing until a real DOM
      // blur arrives, otherwise the later canvas click becomes a global reset.
      if (evt?.type === 'blur') {
        lineHeightEditInteractionRef.current = false;
      }

      // Clicking the canvas clears text editing before this input blurs. Do
      // not reinterpret that blur as an element-wide line-height update: its
      // change event already applied the value to the active Draft selection.
      if (beganWhileEditing) {
        return;
      }
      handleSetLineHeight(value, true);
    },
    [handleSetLineHeight]
  );

  usePresubmitHandler(({ lineHeight: newLineHeight }) => {
    return {
      lineHeight: clamp(newLineHeight, MIN_MAX.LINE_HEIGHT),
    };
  }, []);

  const handleTextAlign = useCallback(
    (alignment) => () => {
      pushUpdate({ textAlign: textAlign === alignment ? '' : alignment }, true);
    },
    [pushUpdate, textAlign]
  );

  const handleVerticalTrim = useCallback(
    (trim) => () => {
      pushUpdate({ verticalTrim: trim }, true);
    },
    [pushUpdate]
  );

  const closeListSettings = useCallback(() => {
    setListSettingsOpen(false);
  }, []);

  const handleListButtonClick = useCallback(() => {
    if (listSettingsOpen) {
      closeListSettings();
      return;
    }

    if (hasList !== true) {
      handleApplyUnorderedList();
    }
    setListSettingsOpen(true);
  }, [closeListSettings, handleApplyUnorderedList, hasList, listSettingsOpen]);

  const handleRemoveList = useCallback(() => {
    handleRemoveListFormatting();
    closeListSettings();
  }, [closeListSettings, handleRemoveListFormatting]);

  // We need to weed out the chance, that either of these are MULTIPLE_VALUE.
  // If not strictly true, treat as false in every way.
  const isTrulyBold = isBold === true;
  const isTrulyItalic = isItalic === true;
  const isTrulyUnderline = isUnderline === true;
  const isTrulyUppercase = isUppercase === true;
  const isListSelectionMixed =
    hasList === MULTIPLE_VALUE ||
    isListMixed === true ||
    isListMixed === MULTIPLE_VALUE;
  const hasTextWithDisabledJustify = selectedElements
    .filter((selectedElement) => elementIs.text(selectedElement))
    .some(
      (selectedTextElement) =>
        hasGradient(selectedTextElement.color) ||
        Boolean(selectedTextElement.textTexture?.src) ||
        hasTextBorder(selectedTextElement.textBorder) ||
        Boolean(getTextDepth(selectedTextElement.textDepth)) ||
        Boolean(getTextInnerGlow(selectedTextElement.textInnerGlow)) ||
        hasMixedTypographyMarkers(selectedTextElement.content || '')
    );

  return (
    <>
      <Row>
        <StyledNumericInput
          aria-label={__('Line-height', 'web-stories')}
          isFloat
          value={lineHeight}
          min={MIN_MAX.LINE_HEIGHT.MIN}
          max={MIN_MAX.LINE_HEIGHT.MAX}
          suffix={<Icons.LetterAHeight />}
          disabled={isInlineLineHeightDisabled}
          title={
            isInlineLineHeightDisabled
              ? __(
                  'Line height is unavailable for list selections',
                  'web-stories'
                )
              : undefined
          }
          onFocus={handleLineHeightFocus}
          onChange={handleLineHeightChange}
          onBlur={handleLineHeightBlur}
          allowEmpty
          isIndeterminate={MULTIPLE_VALUE === lineHeight}
          placeholder={
            MULTIPLE_VALUE === lineHeight ? MULTIPLE_DISPLAY_VALUE : null
          }
          containerStyleOverride={inputContainerStyleOverride}
        />
        <Space />
        <StyledNumericInput
          aria-label={__('Letter-spacing', 'web-stories')}
          value={letterSpacing}
          min={MIN_MAX.LETTER_SPACING.MIN}
          max={MIN_MAX.LETTER_SPACING.MAX}
          suffix={<Icons.LetterAWidth />}
          unit="%"
          onChange={setLetterSpacingMinMax}
          onBlur={setLetterSpacingMinMax}
          allowEmpty
          canBeNegative
          isIndeterminate={MULTIPLE_VALUE === letterSpacing}
          placeholder={
            MULTIPLE_VALUE === letterSpacing ? MULTIPLE_DISPLAY_VALUE : null
          }
          containerStyleOverride={inputContainerStyleOverride}
        />
        <Space />
        <InlineRow>
          <Tooltip
            title={__('Standard vertical trim', 'web-stories')}
            hasTail
            placement={PLACEMENT.TOP}
          >
            <Toggle
              isToggled={!verticalTrim || verticalTrim === 'none'}
              onClick={handleVerticalTrim('none')}
              aria-label={__('Standard vertical trim', 'web-stories')}
            >
              <Icons.TextTrimStandard />
            </Toggle>
          </Tooltip>
          <Tooltip
            title={__('Cap height to baseline vertical trim', 'web-stories')}
            hasTail
            placement={PLACEMENT.TOP}
          >
            <Toggle
              isToggled={verticalTrim === 'baseline'}
              onClick={handleVerticalTrim('baseline')}
              aria-label={__(
                'Cap height to baseline vertical trim',
                'web-stories'
              )}
            >
              <Icons.TextTrimBaseline />
            </Toggle>
          </Tooltip>
        </InlineRow>
      </Row>
      <InlineFormattingRow>
        <Toggle
          isToggled={textAlign === 'left'}
          onClick={handleTextAlign('left')}
          aria-label={__('Align text left', 'web-stories')}
        >
          <Icons.AlignTextLeft />
        </Toggle>
        <Toggle
          isToggled={textAlign === 'center'}
          onClick={handleTextAlign('center')}
          aria-label={__('Align text center', 'web-stories')}
        >
          <Icons.AlignTextCenter />
        </Toggle>
        <Toggle
          isToggled={textAlign === 'right'}
          onClick={handleTextAlign('right')}
          aria-label={__('Align text right', 'web-stories')}
        >
          <Icons.AlignTextRight />
        </Toggle>
        <Toggle
          disabled={hasTextWithDisabledJustify}
          isToggled={textAlign === 'justify'}
          onClick={handleTextAlign('justify')}
          aria-label={__('Align text justified', 'web-stories')}
        >
          <Icons.AlignTextJustified />
        </Toggle>
        <Toggle
          isToggled={isTrulyBold}
          onClick={() => handleClickBold(!isTrulyBold)}
          aria-label={__('Bold', 'web-stories')}
        >
          <Icons.LetterBBold />
        </Toggle>
        <Toggle
          isToggled={isTrulyItalic}
          onClick={async () => {
            await maybeEnqueueFontStyle(
              selectedElements.map(({ font, content }) => {
                return {
                  font,
                  fontStyle: isTrulyItalic ? 'normal' : 'italic',
                  fontWeight,
                  content: stripHTML(content),
                };
              })
            );
            handleClickItalic(!isTrulyItalic);
          }}
          aria-label={__('Italic', 'web-stories')}
        >
          <Icons.LetterIItalic />
        </Toggle>
        <Toggle
          isToggled={isTrulyUnderline}
          onClick={() => handleClickUnderline(!isTrulyUnderline)}
          aria-label={__('Underline', 'web-stories')}
        >
          <Icons.LetterUUnderline />
        </Toggle>
        <Toggle
          isToggled={isTrulyUppercase}
          onClick={() => handleClickUppercase(!isTrulyUppercase)}
          aria-label={__('Uppercase', 'web-stories')}
        >
          <Icons.LetterTUppercase />
        </Toggle>
      </InlineFormattingRow>
      <Row>
        <ListButtonAnchor ref={listButtonRef}>
          <Tooltip
            title={__('Bulleted list', 'web-stories')}
            hasTail
            placement={PLACEMENT.TOP}
          >
            <Toggle
              isToggled={isUnorderedList === true}
              isIndeterminate={isListSelectionMixed}
              onClick={handleListButtonClick}
              aria-label={__('Bulleted list', 'web-stories')}
              aria-haspopup="dialog"
              aria-expanded={listSettingsOpen}
              aria-controls={
                listSettingsOpen ? LIST_SETTINGS_DIALOG_ID : undefined
              }
            >
              <Icons.ListBulleted />
            </Toggle>
          </Tooltip>
        </ListButtonAnchor>
        <Popup
          anchor={listButtonRef}
          dock={inspector}
          isOpen={listSettingsOpen}
          placement={PLACEMENT.LEFT_START}
          spacing={LIST_SETTINGS_SPACING}
          renderContents={() => (
            <ListSettingsPopup
              anchor={listButtonRef}
              onClose={closeListSettings}
            >
              <ListControls
                selectedElements={selectedElements}
                pushUpdate={pushUpdate}
                onRemoveList={handleRemoveList}
              />
            </ListSettingsPopup>
          )}
        />
      </Row>
    </>
  );
}

StylePanel.propTypes = {
  selectedElements: PropTypes.array.isRequired,
  pushUpdate: PropTypes.func.isRequired,
};

export default StylePanel;
